/**
 * THE HEADLINE PRICE DECISION — which source the biggest number on the owner's screen comes from.
 *
 * ⛔⛔ WHY THIS IS A MODULE AND NOT SIX LINES INSIDE `TopStrip.svelte`, WHICH IS WHERE I FIRST PUT IT.
 * GK-s keyed the headline cure (`48d3612d`) and named, in the key itself, exactly what they had NOT
 * established:
 *   · H1 "remove the tickPrice prop" went RED **BY THE TYPECHECKER** — a type removal, not a
 *     behaviour assertion.
 *   · H2 "remove the WIRING at the mount" **DID NOT RUN AT ALL**: the anchor
 *     `tickPrice={tickPayload?.last_price ?? null}` occurs TWICE in `App.svelte` and their harness
 *     REFUSED rather than guess which occurrence to mutate.
 * ⇒ ***A MUTANT THAT DOES NOT APPLY IS A HARNESS FAILURE, NEVER A VERDICT*** (GK-s). The harness was
 * right to refuse — it failed LOUDLY instead of mutating the wrong line and reporting a green that
 * meant nothing. **And the duplicate anchor was mine**: h's `EsChartPanel` prop and my `TopStrip`
 * prop are the same expression, so the file gained two textually identical wirings to two different
 * consumers and neither of us noticed until a tool declined to guess.
 *
 * ⇒ ⭐⭐ THE REAL DEFECT UNDERNEATH BOTH: **the decision lived in a `.svelte` file, which this repo's
 * test runner cannot load.** That is h's own law — *LOGIC PLACED IN A FILE THE TEST RUNNER CANNOT
 * LOAD IS UNARMED BY CONSTRUCTION* — the law `formingBar.ts` was extracted for, applied one file
 * over, to a decision I wrote three hours after quoting it. **The cure is not a cleverer mutant; it
 * is to put the decision where an arm can reach it.**
 *
 * WHAT IT DECIDES, and the ordering is the whole contract:
 *   1 a TICK price, while its OWN age is within budget       -> the live number
 *   2 otherwise the last 1m BAR close                        -> the incumbent path, untouched
 *   3 otherwise nothing                                      -> an em-dash, never a stale number
 * ⛔ THE EM-DASH IS LOAD-BEARING AND MUST SURVIVE EVERY FUTURE EDIT. A stale price is the easiest
 * thing in the world to keep rendering: it is a real number, it was true a minute ago, and it is
 * pixel-identical to a live one. **Declining to print it is the hard half of the kill-a-feed law.**
 */

/** Beyond this, a tick is no longer a claim about NOW and the bar close is preferred. */
export const HEADLINE_TICK_BUDGET_S = 30;

export type HeadlineSource = "tick" | "bar" | null;

export interface Headline {
  /** The number to render, or null for the em-dash. */
  px: number | null;
  /** Which feed it came from. Rendered beside the figure — never inferred by the reader. */
  source: HeadlineSource;
  /** Age of the TICK's own row, seconds. Null when the number is not a tick. */
  ageS: number | null;
}

export interface HeadlineInput {
  /** Tick-level last price, 1e-9-scaled by the route. */
  tickPrice: number | null;
  /**
   * Age of THAT price's own row, seconds, as the route computed it from the row's vendor_ts.
   * ⛔ A tick WITHOUT an age is refused, not trusted: a price the strip cannot place in time
   * silently degrades to "no change", which is a fail-soft on the one figure read at a glance.
   */
  tickAgeS: number | null;
  /** Close of the newest 1m bar, or null when there is none. */
  barClose: number | null;
  budgetS?: number;
}

export function headlinePrice(input: HeadlineInput): Headline {
  const { tickPrice, tickAgeS, barClose, budgetS = HEADLINE_TICK_BUDGET_S } = input;

  // A tick is offered only when it can date itself AND that date is recent. Both halves matter:
  // the age is not a nicety, it is the thing that makes the number a claim about now.
  if (tickPrice !== null && tickAgeS !== null && tickAgeS <= budgetS) {
    return { px: tickPrice, source: "tick", ageS: tickAgeS };
  }
  // The incumbent path, deliberately unchanged: a stale tick does not blank the strip and does not
  // override a good bar — it simply stops being offered.
  if (barClose !== null) return { px: barClose, source: "bar", ageS: null };
  return { px: null, source: null, ageS: null };
}

/**
 * The provenance marker rendered ADJACENT TO THE FIGURE — never only in a chip at the panel edge.
 * ⭐ THE AGE RIDES THE NUMBER: a StalenessChip qualifies a REGION and stays behind when the FIGURE
 * is screenshotted, quoted, glanced at or remembered.
 */
export function headlineMarker(h: Headline): string | null {
  if (h.source === "tick") return `tick · ${h.ageS}s`;
  if (h.source === "bar") return "1m bar close";
  return null;
}
